import { useEffect, useRef, useState } from 'react';
import { CommentIcon } from '../components/icons';
import { cx } from '../components/ui';
import { usePrefersReducedMotion } from '../lib/useReducedMotion';
import type { Comment } from '../lib/types';

/**
 * Comments surfacing as the playhead reaches them, the way reactions do.
 *
 * A comment pinned to a moment is somebody's note about that frame, so it is
 * shown on that frame: a small card with who said it and what, which fades
 * after long enough to be read. Clicking it opens the comment in the
 * Activity panel, where the whole thread is. Unlike a reaction there are
 * words here, so a card lasts longer and only a couple show at once.
 */

const VISIBLE_MS = 4500;
const VISIBLE_MS_STILL = 5500;
/** A jump larger than this is a seek, not playback; nothing scrubbed past fires. */
const MAX_STEP_SEC = 1.5;
const MAX_TEXT = 140;

interface Pop {
  key: string;
  comment: Comment;
}

let popSeq = 0;

export function CommentPops({
  comments,
  currentTime,
  onOpen,
  className,
}: {
  comments: readonly Comment[];
  currentTime: number;
  onOpen?: (comment: Comment) => void;
  className?: string;
}) {
  const reduced = usePrefersReducedMotion();
  const [pops, setPops] = useState<Pop[]>([]);
  const lastTimeRef = useRef(currentTime);
  const commentsRef = useRef(comments);
  commentsRef.current = comments;
  const timersRef = useRef<number[]>([]);

  useEffect(
    () => () => {
      for (const timer of timersRef.current) window.clearTimeout(timer);
      timersRef.current = [];
    },
    [],
  );

  useEffect(() => {
    const previous = lastTimeRef.current;
    lastTimeRef.current = currentTime;
    const step = currentTime - previous;
    if (step <= 0 || step > MAX_STEP_SEC) return;
    const lower = previous === 0 ? -1 : previous;
    const crossed = commentsRef.current.filter(
      (comment) =>
        comment.timecodeSec !== null &&
        comment.timecodeSec !== undefined &&
        comment.timecodeSec > lower &&
        comment.timecodeSec <= currentTime,
    );
    if (crossed.length === 0) return;
    const shown = crossed.slice(0, 2).map((comment) => {
      popSeq += 1;
      return { key: `${comment.id}:${popSeq}`, comment };
    });
    // Newest on top, and never more than three on screen.
    setPops((current) => [...shown, ...current].slice(0, 3));
    const keys = new Set(shown.map((pop) => pop.key));
    const timer = window.setTimeout(
      () => {
        timersRef.current = timersRef.current.filter((id) => id !== timer);
        setPops((current) => current.filter((pop) => !keys.has(pop.key)));
      },
      reduced ? VISIBLE_MS_STILL : VISIBLE_MS,
    );
    timersRef.current.push(timer);
  }, [currentTime, reduced]);

  if (pops.length === 0) return null;

  return (
    <div className={cx('flex w-72 max-w-[70%] flex-col gap-2', className)}>
      {pops.map((pop) => {
        const author = pop.comment.authorUser?.name ?? pop.comment.authorName ?? 'Guest';
        const text =
          pop.comment.bodyText.length > MAX_TEXT ? `${pop.comment.bodyText.slice(0, MAX_TEXT).trimEnd()}…` : pop.comment.bodyText;
        return (
          <button
            key={pop.key}
            type="button"
            onClick={() => onOpen?.(pop.comment)}
            title="Open this comment"
            className={cx(
              'comment-pop pointer-events-auto flex items-start gap-2 rounded-md border border-chalk/15 bg-slate-950/85 px-3 py-2 text-left shadow-raised backdrop-blur',
              'transition-colors duration-120 hover:border-chalk/40',
            )}
            style={{ animationDuration: `${reduced ? VISIBLE_MS_STILL : VISIBLE_MS}ms` }}
          >
            <CommentIcon size={14} className="mt-0.5 shrink-0 text-chalk-dim" />
            <span className="min-w-0">
              <span className="block text-caption font-medium text-chalk">{author}</span>
              <span className="block text-caption leading-snug text-chalk-dim">{text}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
